import * as Yup from 'yup';
import { CollapseSection } from '@/components/ats';
import { DEFAULT_ATTACHMENT_FILES } from '@/constants/attachment-files';
import { EMAIL_REGEX } from '@/utils/emailValidation';
import { CalculatedFieldSync } from './calculatedField/CalculatedFieldSync';
import { FormulaLabelTag } from './calculatedField/FormulaLabelTag';
import { readValueRule } from './calculatedField/fieldValueRule';
import { toDateValue } from '@/api/customFields/formulaWireUnits';
import { colors } from '@/theme/foundations/colors';

const collapseSectionProps = () => ({
  headerConfig: {
    containerStyles: () => ({
      backgroundColor: 'ats-tertiary',
      p: '15px',
    }),
    textStyles: {
      color: 'ats-primary',
    },
    iconStyles: {
      width: '24px',
      height: '24px',
    },
  },
  styles: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '6px',
  },
  collapseStyles: {
    display: 'flex',
    flexDirection: 'column',
    padding: '1em 2em',
    backgroundColor: 'var(--kula-colors-white, #FFFFFF)',
  },
});

const MAX_ALLOWED_NUMBER = 2 ** 49;

// Empty for the purpose of "has the recruiter filled this in yet": a select
// that was never touched serialises to the sentinel 0, and a cleared multi to
// an empty list.
export const isBlankValue = (value: unknown) =>
  value === null ||
  value === undefined ||
  value === '' ||
  value === 0 ||
  value === '0' ||
  (Array.isArray(value) && value.length === 0);

// The key a field's value lives under in the form.
const formFieldName = ({ id, form_name }) => form_name || `custom_${id}`;

// A formula names fields by token; the form keys its values by config name.
// Built from the same config the fields come from, so both stay in step.
const formulaFieldNames = (config) => {
  const byToken = {};
  config?.ats_custom_fields?.forEach(({ id, token, variable, form_name }) => {
    // A static row names itself by `variable` and is keyed in the form by an
    // identifier only its page knows, so the caller passes `form_name`. A
    // custom row has neither and is always `custom_<id>`.
    const key = token || variable;
    if (key)
      byToken[String(key).toLowerCase()] = formFieldName({ id, form_name });
  });
  config?.static_fields?.forEach((section) =>
    section?.fields?.forEach(({ id }) => {
      byToken[String(id).toLowerCase()] = `static_${id}`;
    })
  );
  return (token) => byToken[String(token).toLowerCase()] ?? '';
};

// The configured defaults, keyed the way the config names its fields, so a
// page can seed its initialValues with them. A formula field is left out — it
// calculates its own value rather than holding one.
export const customFieldDefaults = (config) => {
  const seeded = {};
  config?.ats_custom_fields?.forEach((field) => {
    const { default_value, formula } = readValueRule(field);
    if (formula || default_value === null || default_value === undefined)
      return;
    if (Array.isArray(default_value) && default_value.length === 0) return;
    if (default_value === '') return;
    seeded[formFieldName(field)] = default_value;
  });
  return seeded;
};

// A display condition decides whether a field belongs on this offer at all,
// and is re-read as the values it watches are typed. Shared by both pushes
// below: a calculated field returns early from its own branch, so wiring this
// only into the regular one meant a formula field ignored its condition
// entirely and showed on every offer (CORE-5487).
const displayCondition = (
  field: any,
  name: string,
  defaultValue: unknown
): Record<string, unknown> =>
  field?.shows_under
    ? {
        isConditional: true,
        conditionConfig: {
          type: 'custom',
          field: name,
          customFunc: (_value: unknown, values: Record<string, unknown>) =>
            field.shows_under(values),
        },
        // A field hidden when the offer was created carries no saved value, so
        // revealing it later showed an empty box where the admin had set a
        // default. ConditionalFields puts it in the first time the condition
        // lets the field through.
        conditionalDefault: defaultValue,
      }
    : {};

export const getFieldConfig = (
  field,
  isDisabled,
  styleVersion = 'v1',
  labelStyles = {
    fontSize: '14px',
    fontWeight: '650',
  }
) => {
  const {
    id,
    type, // from static fields
    kind, // from custom fields
    multi,
    label,
    options, // labels for enum boolean
    required,
    values = [],
    placeholder,
    description,
    selectable_users = [], // pickable users for rel_user ("Users") fields
  } = field || {};

  const fieldMap = {
    string: {
      type: styleVersion === 'v2' ? 'kInput' : 'input',
      maxLength: 255,
      placeholder,
      disabled: isDisabled,
      labelStyles,
      subLabel: description,
    },
    email: {
      type: styleVersion === 'v2' ? 'kInput' : 'input',
      maxLength: 255,
      placeholder,
      disabled: isDisabled,
      optionalValidation: !required,
      validation: Yup.string()
        .email('Valid email address required (if provided)')
        .matches(EMAIL_REGEX, 'Valid email address required (if provided)')
        .when([], {
          is: () => required,
          then: (schema) => schema.required('Email is required'),
        }),
      labelStyles,
    },
    rel_tag: {
      type: styleVersion === 'v2' ? 'kSelect' : 'select',
      disabled: isDisabled,
      isMulti: multi,
      placeholder,
      options: values
        // sometimes we get deleted filter options based on tha
        ?.filter((opt) => opt?.deleted !== true || opt?.deleted === undefined)
        ?.map((opt) => ({ label: opt?.value, value: opt?.id })),
      // Dropdown fields carried no client-side required rule, so an unselected
      // mandatory dropdown reached the backend and 422'd with the opaque
      // err_processing_form_data. A single-select's empty state serialises to
      // the sentinel 0 / "0" (real option ids are always positive), which
      // constructFieldSchema's default Yup.string().required() reads as filled —
      // so validate the sentinel explicitly. Multi-selects keep the default
      // array required + min(1) rule. `validation` only applies when the field
      // is required (constructFieldSchema gates on isRequired). (ENG-3456)
      ...(multi
        ? {}
        : {
            validation: Yup.mixed().test(
              'dropdown-required',
              `${label} is required`,
              (value) =>
                value !== undefined &&
                value !== null &&
                value !== '' &&
                value !== 0 &&
                value !== '0'
            ),
          }),
      labelStyles,
      subLabel: description,
    },
    // "Users" field — multi-select limited to the field's selectable users.
    // Options are the user cards ({ id, name, avatar }); the value binds to the
    // user id. The caller (page layer) attaches `customFormatLabel:
    // SelectOptionWithAvatar` so both the menu and the selected chips show
    // avatars — a component import isn't allowed inside this util.
    rel_user: {
      type: styleVersion === 'v2' ? 'kSelect' : 'select',
      disabled: isDisabled,
      isMulti: true,
      placeholder,
      options: selectable_users,
      valueKey: 'id',
      labelKey: 'name',
      avatarKey: 'avatar',
      labelStyles,
      subLabel: description,
    },
    text: {
      type: 'textArea',
      disabled: isDisabled,
      isRequired: required,
      placeholder,
      validation: Yup.string()
        .trim()
        .max(10000, 'Max 10,000 characters allowed')
        .when([], {
          is: () => required,
          then: (schema) => schema.required(`${label} is required`),
        }),
      styleVersion: styleVersion,
      labelStyles,
      subLabel: description,
    },
    number: {
      type: styleVersion === 'v2' ? 'kInput' : 'input',
      inputType: 'number',
      fieldType: 'number',
      disabled: isDisabled,
      placeholder,
      validation: Yup.string()
        .test('number', 'Please enter a valid number', (value) => {
          if (!value) return true;
          // Strip cosmetic separators so "100,000" and "1.000.000" validate
          // as numbers. Plain numeric values are unaffected.
          const normalized = value.toString().replace(/,/g, '');
          const dotCount = (normalized.match(/\./g) || []).length;
          const forNumberCheck =
            dotCount > 1 ? normalized.replace(/\./g, '') : normalized;
          return !isNaN(Number(forNumberCheck));
        })
        .test('float', 'Max 4 decimal places allowed', (value) => {
          if (!value) return true;
          const str = value.toString();
          const dotCount = (str.match(/\./g) || []).length;
          // Multi-dot values are separator-formatted (e.g. 1.000.000);
          // skip the decimal-places cap.
          if (dotCount > 1) return true;
          if (str.includes('.')) {
            const [, decimal = ''] = str.split('.');
            return decimal.length <= 4;
          }
          return true;
        })
        .test('length', 'Max limit reached', (value) => {
          if (!value) return true;
          const str = value.toString();
          const hasComma = str.includes(',');
          const dotCount = (str.match(/\./g) || []).length;
          const normalized =
            hasComma || dotCount > 1 ? str.replace(/[,.]/g, '') : str;
          const numberValue = Number(normalized);
          return numberValue <= MAX_ALLOWED_NUMBER;
        })
        .when([], {
          is: () => required,
          then: (schema) => schema.required(`${label} is required`),
        }),
      labelStyles,
      subLabel: description,
    },
    phone: {
      type: styleVersion === 'v2' ? 'kInput' : 'input',
      maxLength: 255,
      disabled: isDisabled,
      placeholder,
      labelStyles,
      subLabel: description,
    },
    date: {
      type: 'date',
      disabled: isDisabled,
      placeholder,
      // Not `dayjs(date)`: a date crosses the wire as an epoch in a STRING,
      // and dayjs reads a 13-digit string as a year — "1794355200000" became
      // 22/12/1796 rather than 11/11/2026, which is what a calculated date
      // showed on the offer form (CORE-5478).
      dateFormat: (date) => toDateValue(date),
      isRequired: required,
      styleVersion: styleVersion,
      sendDateInUTC: true,
      labelStyles,
      subLabel: description,
    },
    enum_boolean: {
      type: 'radio2',
      disabled: isDisabled,
      options: options?.labels
        ? [
            {
              label: options?.labels?.Y,
              value: 'Y',
            },
            {
              label: options?.labels?.N,
              value: 'N',
            },
          ]
        : [
            {
              label: 'Yes',
              value: 'Y',
            },
            {
              label: 'No',
              value: 'N',
            },
          ],
      labelStyles,
      subLabel: description,
      placeholder,
    },
    url: {
      type: styleVersion === 'v2' ? 'kInput' : 'input',
      disabled: isDisabled,
      optionalValidation: required,
      maxLength: 250,
      validation: Yup.string()
        .matches(
          /^(https?:\/\/)?([\w-]+(\.[\w-]+)*\.[\w-]{2,})(:\d+)?(\/\S*)?$/,
          'Please enter a valid URL'
        )
        .when([], {
          is: () => required,
          then: (schema) => schema.required('URL is required'),
        })
        .trim()
        .max(250, 'Max 250 characters allowed'),

      isRequired: required,
      labelStyles,
      subLabel: description,
      placeholder,
    },
    currency: {
      disabled: isDisabled,
      type: styleVersion === 'v2' ? 'kInput' : 'input',
      placeholder,
      formatType: 'currency',
      labelStyles,
      subLabel: description,
    },
    richtext: {
      type: 'editor2',
      disabled: isDisabled,
      placeholder,
      isRequired: required,
      pluginsConfig: ['table'],
      printFormat: true,
      containerStyles: {
        width: '100%',
      },
      editorStyles: {
        height: '300px',
      },
      toolBarProps: {
        containerProps: {
          paddingTop: '0 !important',
        },
      },
      subLabel: description,
    },
    rel_file: (() => {
      let acceptedFiles = DEFAULT_ATTACHMENT_FILES.join(',');
      let description = undefined;

      switch (id) {
        case 'photo':
          acceptedFiles = '.jpeg,.png';
          description = 'Supported : .jpeg, .png';
          break;
        case 'resume':
        case 'cover_letter':
          acceptedFiles = '.pdf,.docx,.doc';
          description = 'Supported : .pdf, .docx, .doc';
          break;
        default:
          break;
      }

      return {
        disabled: isDisabled,
        placeholder,
        type: 'upload',
        acceptedFiles,
        description: description,
        errorMessage: `${label} is required`,
        labelStyles,
        subLabel: description,
      };
    })(),
  };

  return fieldMap?.[type || kind] || {};
};

export const generateCustomFormConfig = (
  config,
  splitName = false,
  isDisabled = false,
  tokenPrefix = '',
  styleVersion = 'v1',
  labelStyles = {
    fontSize: '14px',
    fontWeight: '650',
  }
) => {
  const {
    ats_custom_fields = [],
    field_groups = {},
    static_fields = [],
  } = config || {};

  const formConfig = {
    static: [],
    custom: [],
  };

  //static fields
  static_fields?.forEach((section, index) => {
    formConfig.static.push({
      id: section?.id,
      type: 'wrapper',
      fields: [],
      Wrapper: CollapseSection,
      wrapperProps: {
        title: section?.label,
        isHeaderClickable: true,
        defaultIsOpen: true,
        ...collapseSectionProps(),
      },
    });

    const currentItem = formConfig.static?.[index]?.fields;

    section?.fields?.forEach((field) => {
      const { hidden, required, id, label, type } = field;
      const otherItemsBasedOnType =
        getFieldConfig(field, isDisabled, styleVersion, labelStyles) || {};

      //handle complex type
      if (type === 'complex') {
        //need to handle complex data
      }

      // name
      else if (id === 'name' && splitName) {
        currentItem.push({
          name: `static_first_name`,
          label: 'First name',
          maxLength: 255,
          isRequired: required,
          ...otherItemsBasedOnType,
        });
        currentItem.push({
          name: `static_last_name`,
          label: 'Last name',
          maxLength: 255,
          // Last name is always optional even when the backend marks the
          // combined "name" field as required — only first name is mandatory.
          isRequired: false,
          ...otherItemsBasedOnType,
        });
      } else if (!hidden) {
        currentItem.push({
          name: `static_${id}`,
          label,
          isRequired: required,
          ...otherItemsBasedOnType,
        });
      }
    });
  });

  //custom fields

  Object?.entries(field_groups).forEach(([key, value]) => {
    formConfig.custom.push({
      id: key,
      type: 'wrapper',
      fields: [],
      Wrapper: CollapseSection,
      wrapperProps: {
        title: value,
        isHeaderClickable: true,
        defaultIsOpen: true,
        ...collapseSectionProps(),
      },
    });
  });

  const fieldNameOf = formulaFieldNames(config);

  ats_custom_fields?.forEach((field) => {
    const { hidden, required, id, label, field_group_idx, token = '' } = field;
    // The API carries these on value_rule, not as flat keys.
    const { formula, locked, default_value } = readValueRule(field);
    // What the form calls this field. A static row is keyed by the identifier
    // its own page uses, which only that page knows, so it passes `form_name`;
    // a custom row is always `custom_<id>`. Keyed the other way, a static
    // field's computed value never reaches the offer payload and its token
    // reads as unfilled when the letter is generated.
    const name = formFieldName(field);
    const otherItemsBasedOnType =
      getFieldConfig(field, isDisabled, styleVersion, labelStyles) || {};

    let currentItem;
    if (field_group_idx in field_groups) {
      currentItem = formConfig.custom?.[field_group_idx - 1]?.fields;
    } else {
      currentItem = formConfig.custom;
    }

    if (hidden) return;

    // Offer-only by data, not by a check here: every other subject is refused a
    // value_rule by the backend (err_value_rule_subject_unsupported), so no
    // requisition, job, candidate or job-profile field reaches this branch.
    //
    // A formula field calculates its own value and recomputes as the fields it
    // reads change. It keeps the control its kind would have had — a Yes/No
    // field stays the Yes/No control, a date stays the picker — and the result
    // is written into form state so that control shows it. Rendering a text
    // box instead lost the control, the currency selector and the editor, and
    // showed a Yes/No field the raw letter it stores.
    if (formula) {
      const isLocked = locked !== false;
      currentItem.push({
        ...otherItemsBasedOnType,
        name,
        label,
        // Lets a page that renders this field through its own config (the offer
        // form does, for static fields) tell a calculated item from a plain one.
        isCalculated: true,
        // Without this the offer letter's token check skips the field — it
        // reads only fields carrying a tokenName — so a formula that computed
        // perfectly well still reported "Fields not filled for tokens".
        tokenName: `${tokenPrefix}.${token}`,
        // A locked result is not the filler's to type, and is not held to a
        // format they could not correct either: a Mandatory url field keeps its
        // regex through `optionalValidation`, so a formula that worked out
        // something that is not a link blocked the recruiter on a field they
        // cannot edit (CORE-5477).
        //
        // Mandatory still means mandatory. It used to be forced off here so a
        // locked field could not trap the filler, but a setting that quietly
        // turns itself off is worse: the offer saved without a value the admin
        // had said was required. It now blocks, and says the formula is why,
        // so the admin can go and fix it (Venil and Daniel, CORE-5487).
        ...(isLocked
          ? {
              disabled: true,
              optionalValidation: false,
              errorMessage: `"${label}" is required, but its formula couldn\u2019t calculate a value. Fill in the fields it depends on.`,
            }
          : {}),
        labelBadge: (
          <FormulaLabelTag
            formula={formula}
            locked={isLocked}
            invalid={field.rule_state?.state === 'error'}
          />
        ),
        // A calculated field can be conditional too. It returns from this
        // branch, so without this its condition was never wired and the field
        // showed on every offer (CORE-5487). No conditionalDefault: a formula
        // works its own value out rather than starting at one.
        ...displayCondition(field, name, undefined),
        showAfterContent: true,
        // The component itself, with its props beside it, rather than an
        // inline `() => <CalculatedFieldSync …/>`. This config is rebuilt on
        // every render, so a closure here was a new component type each time
        // and React remounted the whole subtree — which drops whatever the
        // recruiter had focused, and inside the preview drawer costs the
        // first click outright (CORE-5469).
        AfterContentsComp: CalculatedFieldSync,
        afterContentsProps: {
          name,
          formula,
          fieldNameOf,
          // The server checks the result against the field it writes to.
          kind: field.kind,
          fieldId: id,
          // A select answers with its option names; its control holds ids.
          options: otherItemsBasedOnType.options,
          multi: field.multi,
          // The offer's own verdict on this rule: whether it computed, and
          // which fields a blank one is still waiting on.
          state: field.rule_state?.state,
          // Whole entries, not just labels: the `field_id` is what says
          // whether the recruiter can do anything about a blocker.
          blockedBy: field.rule_state?.blocked_by,
          // Nothing says otherwise means locked: never widen edit rights by default.
          locked: isLocked,
        },
      });
      return;
    }

    currentItem.push({
      name,
      label,
      isRequired: required,
      tokenName: `${tokenPrefix}.${token}`,
      ...otherItemsBasedOnType,
      // "Prevent edits": the default stands and the filler can't change it.
      // The badge says so beside the label — a greyed input on its own reads
      // as broken rather than as settled.
      ...(locked ? { disabled: true, locked: true } : {}),
      // A formula is waiting on this field. The formula already says what it
      // wants; this is the end the recruiter can do something about.
      //
      // Read against the values as they are typed, not once at load: the
      // server's blocked_by is its verdict on the offer as it was fetched, so
      // a static note stayed red under a field the recruiter had just filled.
      // The sibling case, hidden_by_condition, is re-read the same way.
      ...(field.needed_for
        ? {
            subLabel: (values: Record<string, unknown>) =>
              isBlankValue(values?.[name])
                ? `This field is needed to compute the "${field.needed_for}" field`
                : undefined,
            subLabelStyles: { color: colors['pastel-1-dark'] },
          }
        : {}),
      // A display condition decides whether this field belongs on the offer at
      // all, and has to be re-read as the values it watches are typed.
      ...displayCondition(field, name, default_value),
    });
  });

  return formConfig;
};
